import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { logoSrc } from "services/transportadoraLogoApi";
import { AUTOPILOT_COLORS, autopilotInsetCardStyle } from "../../../../components/autopilot/styles";
import { StatusPill } from "../capacidadeBadges";
import TransportadoraMonogram from "../TransportadoraMonogram";
import { ROTULO_CATEGORIA, ROTULO_MODALIDADE, rotuloCobertura } from "./filtros";

// Card do grid de catálogo (H2, PO1-2620). Checkbox de seleção múltipla +
// identidade + badges de fonte/capacidade + CTA "Adicionar" individual.
// PO1-2727: tudo por token; selecionado ganha borda/fundo da cor de destaque,
// "Adicionar" em estilo secundário (sem cor chapada).
// PO1-3317: card maior, no padrão do ConectorCard do catálogo de conectores do
// embarcador: logo em faixa no topo (fundo branco fixo, para a logo de terceiro
// aparecer nos dois temas), nome e documento, badges e ações no rodapé.
// PO1-3856/3857: a pill de fonte virou pill de categoria do conector; com rota
// ativa entra a pill de cobertura (comprovada/declarada); modalidade (CNAE) na
// linha do documento.

const FAIXA_ALTURA = 96;
const FAIXA_MARGEM_LATERAL = 44;

// Rótulo de cada capacidade (sufixo do cap_* do conector).
export const ROTULO_CAPACIDADE = {
  cotacao: "Cotação",
  tracking: "Rastreio",
  coleta: "Coleta",
  etiqueta: "Etiqueta", // PO1-2570
};

const formatDoc = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return v || "-";
};

const cardStyle = {
  ...autopilotInsetCardStyle,
  padding: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  cursor: "pointer",
  position: "relative",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
};

const corpoStyle = {
  padding: "0.8rem 1rem 0.9rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.6rem",
  flex: 1,
  minWidth: 0,
};

const cardSelectedStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.accent}`,
  background: AUTOPILOT_COLORS.insetSurfaceActive,
  boxShadow: "var(--everlog-card-active-glow)",
};

// PO1-3167: card ABERTO no drawer é outro estado, diferente de "selecionado
// pelo checkbox" — os dois coexistem no Catálogo. Borda interna de destaque
// (mesmo padrão de linha ativa de Admin/Transportadoras) por cima do glow.
const cardAbertoStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.accent}`,
  boxShadow: `inset 0 0 0 1px ${AUTOPILOT_COLORS.accent}, var(--everlog-card-active-glow)`,
};

const detalheButtonStyle = {
  background: "transparent",
  border: `1px solid ${AUTOPILOT_COLORS.borderFaint}`,
  color: AUTOPILOT_COLORS.text,
  fontWeight: 600,
  boxShadow: "none",
  whiteSpace: "nowrap",
  padding: "0.45rem 0.5rem",
};

// Botão secundário tokenizado: transparente com borda/texto na cor de destaque.
const addButtonStyle = {
  background: "transparent",
  border: "1px solid var(--theme-accent-border)",
  color: AUTOPILOT_COLORS.accent,
  fontWeight: 600,
  boxShadow: "none",
  whiteSpace: "nowrap",
  padding: "0.45rem 0.5rem",
};

const addedButtonStyle = {
  background: "transparent",
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  color: AUTOPILOT_COLORS.muted,
  boxShadow: "none",
  opacity: 1,
  whiteSpace: "nowrap",
  padding: "0.45rem 0.5rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Checkbox sobre a faixa: fica numa plaqueta para continuar visível em cima da
// logo (fundo branco) e do monograma.
const checkboxPlaquetaStyle = {
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: 7,
  background: AUTOPILOT_COLORS.insetSurface,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  cursor: "pointer",
  margin: 0,
};

// Faixa de logo do topo do card. Com logo: imagem no fundo branco fixo (logo de
// terceiro vem em cor chapada e some no tema escuro, PO1-2722). Sem logo, ou se
// a imagem falhar: monograma grande centralizado.
function LogoFaixa({ nome, logoUrl }) {
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    setFalhou(false);
  }, [logoUrl]);

  const src = logoUrl && !falhou ? logoSrc(logoUrl) : null;
  if (!src) {
    return (
      <div
        style={{
          height: FAIXA_ALTURA,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid var(--everlog-card-border-faint)",
        }}
      >
        <TransportadoraMonogram nome={nome} size={52} />
      </div>
    );
  }
  return (
    <div
      style={{
        width: "100%",
        height: FAIXA_ALTURA,
        background: "#ffffff", // theme-ok: fundo branco fixo atrás de logo de terceiro (PO1-2722, decisão 7)
        borderBottom: "1px solid var(--everlog-card-border-faint)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        // Margem lateral maior que a plaqueta do checkbox (8 + 26 px), dos dois
        // lados para o logo continuar centrado: logo largo não fica por baixo dele.
        padding: `14px ${FAIXA_MARGEM_LATERAL}px`,
        boxSizing: "border-box",
      }}
    >
      <img
        src={src}
        alt={`Logo ${nome}`}
        onError={() => setFalhou(true)}
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
      />
    </div>
  );
}

// onAbrirDetalhe fica no corpo do card; checkbox e rodapé param a propagação
// no próprio handler, senão o clique nunca chega no card (ou o card rouba o
// clique dos controles). `aberto` = este card é o que está no drawer.
export default function CatalogoCard({ item, selecionado, aberto, onToggleSelecionado, onAdicionar, adicionando, onAbrirDetalhe }) {
  const nome = item.fantasia || item.razaoSocial;
  const capacidades = Array.isArray(item.capacidades) ? item.capacidades : [];
  const semConector = !item.categoria || item.categoria === "sem_conector";
  const rotuloCategoria = semConector ? "Sem conector" : ROTULO_CATEGORIA[item.categoria] || item.categoria;
  const modalidade = item.modalidade && item.modalidade !== "nao_informado" ? ROTULO_MODALIDADE[item.modalidade] || item.modalidade : null;
  const cobertura = rotuloCobertura(item.cobertura);
  return (
    <div
      className={`catalogo-card${selecionado ? " is-selected" : ""}${aberto ? " is-open" : ""}`}
      style={{ ...cardStyle, ...(selecionado ? cardSelectedStyle : null), ...(aberto ? cardAbertoStyle : null) }}
      onClick={onAbrirDetalhe}
    >
      <label style={checkboxPlaquetaStyle} onClick={(e) => e.stopPropagation()} title={item.jaAdicionada ? "Já está na sua carteira" : "Selecionar"}>
        <input
          type="checkbox"
          checked={!!selecionado}
          disabled={item.jaAdicionada}
          onChange={() => onToggleSelecionado(item.id)}
          style={{ margin: 0, accentColor: "var(--theme-accent)" }}
          aria-label={`Selecionar ${nome}`}
        />
      </label>

      <LogoFaixa nome={nome} logoUrl={item.logoUrl} />

      <div style={corpoStyle}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: AUTOPILOT_COLORS.title,
              fontSize: "0.95rem",
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={nome}
          >
            {nome}
          </div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem", marginTop: 2 }}>
            {formatDoc(item.cnpjCpf)}
            {item.uf ? ` · ${item.uf}` : ""}
            {modalidade ? ` · ${modalidade}` : ""}
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          <StatusPill status={semConector ? "sem-conector" : "categoria"} labelOverride={rotuloCategoria} />
          {cobertura && <StatusPill status={item.cobertura.nivel === "comprovada" ? "ativo" : "pendente"} labelOverride={cobertura} />}
          {capacidades.map((cap) => (
            <StatusPill key={cap} status="pendente" labelOverride={ROTULO_CAPACIDADE[cap] || cap} />
          ))}
        </div>

        <div style={{ marginTop: "auto", display: "flex", gap: "0.4rem" }} onClick={(e) => e.stopPropagation()}>
          <Button type="button" size="sm" style={{ ...detalheButtonStyle, flex: 1, minWidth: 0 }} onClick={onAbrirDetalhe} title="Ver detalhes desta transportadora" aria-pressed={!!aberto}>
            Detalhes
          </Button>
          {item.jaAdicionada ? (
            <Button type="button" size="sm" disabled style={{ ...addedButtonStyle, flex: 1, minWidth: 0 }} title="Já está na sua carteira">
              Na carteira
            </Button>
          ) : (
            <Button type="button" size="sm" disabled={adicionando} style={{ ...addButtonStyle, flex: 1, minWidth: 0 }} onClick={() => onAdicionar(item.id)}>
              {adicionando ? "Adicionando..." : "Adicionar"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
